import type {
  CreateResultTemplateColumnInput,
  CreateResultTemplateFeedbackInput,
  CreateResultTemplateInput,
  Grade,
} from '@/types/composition-functions';

/**
 * Pure logic for the conversational result-template wizard.
 * Keeps payload composition free of Vue/Apollo so it can be unit tested.
 */

export interface WizardAssessmentEntry {
  name: string;
  maxScore: number;
  isExam: boolean;
  /** Set when the entry matches an existing reusable assessment type. */
  existingId: string | null;
}

/**
 * Structural subset of SchoolAssessmentType, so query-result shapes fit.
 * Reusable assessments are the school-wide ones (no academic_calendar_id) —
 * the wizard only ever matches against and creates these.
 */
export interface ReusableAssessmentLike {
  id: string;
  name: string;
  max_score: number;
  is_exam: boolean;
  academic_calendar_id?: string | null;
}

export interface WizardFeedbackEntry {
  label: string;
  category: string | null;
}

export type WizardGradeChoice = 'grade_remark' | 'grade' | 'none';
export type WizardStyleCode = 'classic' | 'modern' | 'detailed';

export interface WizardAnswers {
  name: string;
  assessments: WizardAssessmentEntry[];
  includeTotal: boolean;
  gradeChoice: WizardGradeChoice;
  gradeGroupId: string | null;
  includeGpaPack: boolean;
  feedbacks: WizardFeedbackEntry[];
  styleCode: WizardStyleCode;
  includeOverallPercentage: boolean;
}

export interface WizardColumnDraft {
  label: string;
  key: string;
  type: string;
  maxScore?: number;
  calculation_function?: string | null;
  calculation_arguments?: unknown;
}

export interface WizardSummaryDraft {
  label: string;
  key: string;
  calculation_function: string;
  source_key: string;
  max_score?: number;
  unit_key?: string;
  weighted_grade_point_key?: string;
}

export interface AssessmentPreset {
  id: string;
  label: string;
  description: string;
  entries: WizardAssessmentEntry[];
}

/** Keys generated by the wizard itself — assessment keys must never collide with these. */
const RESERVED_KEYS = ['total', 'grade', 'remark', 'unit', 'grade_point', 'weighted_grade_point'];

export const ASSESSMENT_PRESETS: AssessmentPreset[] = [
  {
    id: 'ca2_exam',
    label: '2 CAs + Exam',
    description: 'CA 1 (20) · CA 2 (20) · Exam (60)',
    entries: [
      { name: 'CA 1', maxScore: 20, isExam: false, existingId: null },
      { name: 'CA 2', maxScore: 20, isExam: false, existingId: null },
      { name: 'Exam', maxScore: 60, isExam: true, existingId: null },
    ],
  },
  {
    id: 'ca1_exam',
    label: '1 CA + Exam',
    description: 'CA (40) · Exam (60)',
    entries: [
      { name: 'CA', maxScore: 40, isExam: false, existingId: null },
      { name: 'Exam', maxScore: 60, isExam: true, existingId: null },
    ],
  },
  {
    id: 'ca3_exam',
    label: '3 CAs + Exam',
    description: 'CA 1 (15) · CA 2 (15) · CA 3 (10) · Exam (60)',
    entries: [
      { name: 'CA 1', maxScore: 15, isExam: false, existingId: null },
      { name: 'CA 2', maxScore: 15, isExam: false, existingId: null },
      { name: 'CA 3', maxScore: 10, isExam: false, existingId: null },
      { name: 'Exam', maxScore: 60, isExam: true, existingId: null },
    ],
  },
  {
    id: 'exam_only',
    label: 'Exam only',
    description: 'Exam (100)',
    entries: [
      { name: 'Exam', maxScore: 100, isExam: true, existingId: null },
    ],
  },
  {
    id: 'custom',
    label: 'Something else',
    description: 'Define your own score breakdown',
    entries: [
      { name: 'CA 1', maxScore: 20, isExam: false, existingId: null },
      { name: 'Exam', maxScore: 60, isExam: true, existingId: null },
    ],
  },
];

export const FEEDBACK_PRESETS: WizardFeedbackEntry[] = [
  { label: 'Class Teacher\'s Comment', category: 'general' },
  { label: 'Principal\'s Comment', category: 'general' },
  { label: 'Head Teacher\'s Comment', category: 'general' },
];

export function slugifyKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function deriveTemplateCode(name: string): string {
  return name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 32)
    .replace(/_+$/g, '');
}

export function deriveFeedbackCode(label: string): string {
  return label
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/** Unique, reserved-safe keys for the assessment entries, in order. */
export function deriveAssessmentKeys(assessments: WizardAssessmentEntry[]): string[] {
  const taken = new Set<string>(RESERVED_KEYS);
  return assessments.map((entry) => {
    const base = slugifyKey(entry.name) || 'score';
    let key = base;
    let suffix = 2;
    while (taken.has(key)) {
      key = `${base}_${suffix}`;
      suffix += 1;
    }
    taken.add(key);
    return key;
  });
}

export function totalMaxScore(assessments: WizardAssessmentEntry[]): number {
  return assessments.reduce((sum, entry) => sum + (entry.maxScore || 0), 0);
}

/** The column grades/GPA columns read from: Total when present, else the last assessment. */
export function gradeSourceKey(answers: WizardAnswers): string | null {
  if (answers.includeTotal) return 'total';
  const keys = deriveAssessmentKeys(answers.assessments);
  return keys[keys.length - 1] ?? null;
}

export function needsGradeScale(answers: WizardAnswers, isTertiary: boolean): boolean {
  return answers.gradeChoice !== 'none' || (isTertiary && answers.includeGpaPack);
}

export function buildColumnDrafts(answers: WizardAnswers, isTertiary: boolean): WizardColumnDraft[] {
  const drafts: WizardColumnDraft[] = [];
  const keys = deriveAssessmentKeys(answers.assessments);

  answers.assessments.forEach((entry, index) => {
    drafts.push({
      label: entry.name.trim(),
      key: keys[index] ?? 'score',
      type: 'ASSESSMENT',
      maxScore: entry.maxScore,
    });
  });

  if (answers.includeTotal && keys.length > 0) {
    drafts.push({
      label: 'Total',
      key: 'total',
      type: 'CALCULATION',
      calculation_function: 'SUM',
      // The existing builder serialises CALCULATION arguments as a JSON string.
      calculation_arguments: JSON.stringify(keys),
    });
  }

  const source = gradeSourceKey(answers);

  if (answers.gradeChoice !== 'none' && source) {
    drafts.push({
      label: 'Grade',
      key: 'grade',
      type: 'GRADE',
      calculation_arguments: { source, output: 'grade' },
    });
    if (answers.gradeChoice === 'grade_remark') {
      drafts.push({
        label: 'Remark',
        key: 'remark',
        type: 'GRADE',
        calculation_arguments: { source, output: 'remark' },
      });
    }
  }

  if (isTertiary && answers.includeGpaPack && source) {
    drafts.push({ label: 'Unit', key: 'unit', type: 'UNIT', calculation_arguments: null });
    drafts.push({
      label: 'Grade Point',
      key: 'grade_point',
      type: 'GRADE_POINT',
      calculation_arguments: { source },
    });
    drafts.push({
      label: 'Weighted GP',
      key: 'weighted_grade_point',
      type: 'WEIGHTED_GRADE_POINT',
      calculation_arguments: { unit_key: 'unit', grade_point_key: 'grade_point' },
    });
  }

  return drafts;
}

export function buildSummaryDrafts(answers: WizardAnswers, isTertiary: boolean): WizardSummaryDraft[] {
  const summaries: WizardSummaryDraft[] = [];

  if (answers.includeTotal && answers.includeOverallPercentage && answers.assessments.length > 0) {
    summaries.push({
      label: 'Overall Percentage',
      key: 'overall_percentage',
      calculation_function: 'PERCENTAGE',
      source_key: 'total',
      max_score: totalMaxScore(answers.assessments),
    });
  }

  if (isTertiary && answers.includeGpaPack) {
    summaries.push({
      label: 'GPA',
      key: 'gpa',
      calculation_function: 'GPA',
      source_key: 'weighted_grade_point',
      unit_key: 'unit',
      weighted_grade_point_key: 'weighted_grade_point',
    });
    summaries.push({
      label: 'CGPA',
      key: 'cgpa',
      calculation_function: 'CGPA',
      source_key: 'weighted_grade_point',
      unit_key: 'unit',
      weighted_grade_point_key: 'weighted_grade_point',
    });
  }

  return summaries;
}

export function buildFeedbackInputs(feedbacks: WizardFeedbackEntry[]): CreateResultTemplateFeedbackInput[] {
  return feedbacks.map((entry, index) => ({
    label: entry.label.trim(),
    code: deriveFeedbackCode(entry.label),
    category: entry.category,
    sequence: index,
  }));
}

/**
 * Compose the CreateResultTemplateInput payload.
 * `assessmentIdByKey` maps each ASSESSMENT column key to a resolved
 * reusable SchoolAssessmentType id (existing or freshly created).
 */
export function buildCreateTemplateInput(
  answers: WizardAnswers,
  isTertiary: boolean,
  assessmentIdByKey: Record<string, string>,
): CreateResultTemplateInput {
  const drafts = buildColumnDrafts(answers, isTertiary);
  const summaries = buildSummaryDrafts(answers, isTertiary);

  const properties: Record<string, unknown> = {};
  if (summaries.length > 0) properties.summaries = summaries;
  if (answers.styleCode === 'detailed') {
    properties.chart = { type: 'radar', column: gradeSourceKey(answers) || '' };
  }

  const columns: CreateResultTemplateColumnInput[] = drafts.map((draft, index) => ({
    label: draft.label,
    key: draft.key,
    type: draft.type,
    sequence: index,
    is_visible: true,
    school_assessment_type_id:
      draft.type === 'ASSESSMENT' ? assessmentIdByKey[draft.key] ?? null : null,
    calculation_function: draft.calculation_function ?? null,
    calculation_arguments: draft.calculation_arguments ?? null,
  }));

  return {
    name: answers.name.trim(),
    code: deriveTemplateCode(answers.name),
    style_code: answers.styleCode,
    grade_group_id: needsGradeScale(answers, isTertiary) ? answers.gradeGroupId : null,
    properties: Object.keys(properties).length > 0 ? properties : null,
    columns,
    feedbacks: buildFeedbackInputs(answers.feedbacks),
  };
}

/** Normalise an assessment name for matching: 'CA 2', 'ca-2' and 'CA2' are the same. */
export function normalizeAssessmentName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Match an entry against existing REUSABLE (school-wide, not term-bound)
 * assessment types by normalised name. Term-bound assessments are never
 * matched, so a wizard template keeps working across academic calendars.
 */
export function matchExistingAssessment<T extends ReusableAssessmentLike>(
  name: string,
  existing: T[],
): T | null {
  const needle = normalizeAssessmentName(name);
  if (!needle) return null;
  return existing.find(
    (type) => !type.academic_calendar_id && normalizeAssessmentName(type.name) === needle,
  ) || null;
}

// --- Live preview ----------------------------------------------------------

export interface PreviewGradeInfo {
  grade: string;
  remark: string;
  point: number;
}

const FALLBACK_BANDS: Array<{ min: number; grade: string; remark: string; point: number }> = [
  { min: 70, grade: 'A', remark: 'Excellent', point: 5 },
  { min: 60, grade: 'B', remark: 'Very Good', point: 4 },
  { min: 50, grade: 'C', remark: 'Good', point: 3 },
  { min: 45, grade: 'D', remark: 'Fair', point: 2 },
  { min: 40, grade: 'E', remark: 'Pass', point: 1 },
  { min: 0, grade: 'F', remark: 'Fail', point: 0 },
];

export function gradeForScore(score: number, bands: Grade[] | null): PreviewGradeInfo {
  if (bands && bands.length > 0) {
    const band = bands.find(
      (b) => score >= (b.minimum ?? 0) && score <= (b.maximum ?? 100),
    );
    if (band) {
      return {
        grade: band.grade || '—',
        remark: band.name || band.grade || '—',
        point: band.point ?? 0,
      };
    }
  }
  const fallback = FALLBACK_BANDS.find((b) => score >= b.min);
  if (!fallback) return { grade: 'F', remark: 'Fail', point: 0 };
  return { grade: fallback.grade, remark: fallback.remark, point: fallback.point };
}

export interface PreviewSubjectSeed {
  subject: string;
  ratio: number;
  unit: number;
}

export const PREVIEW_SUBJECT_SEEDS: PreviewSubjectSeed[] = [
  { subject: 'Mathematics', ratio: 0.85, unit: 3 },
  { subject: 'English Language', ratio: 0.72, unit: 2 },
  { subject: 'Basic Science', ratio: 0.64, unit: 2 },
];

/** Sample table rows for the live preview, keyed by column key plus `subject`. */
export function buildPreviewRows(
  columns: WizardColumnDraft[],
  bands: Grade[] | null,
): Array<Record<string, string | number>> {
  return PREVIEW_SUBJECT_SEEDS.map((seed) => {
    const row: Record<string, string | number> = { subject: seed.subject };
    let total = 0;

    columns.forEach((column) => {
      if (column.type === 'ASSESSMENT') {
        const score = Math.round((column.maxScore || 0) * seed.ratio);
        row[column.key] = score;
        total += score;
      }
    });

    const gradeInfo = gradeForScore(total, bands);

    columns.forEach((column) => {
      switch (column.type) {
      case 'CALCULATION':
        row[column.key] = total;
        break;
      case 'GRADE': {
        const args = column.calculation_arguments as { output?: string } | null;
        row[column.key] = args?.output === 'remark' ? gradeInfo.remark : gradeInfo.grade;
        break;
      }
      case 'UNIT':
        row[column.key] = seed.unit;
        break;
      case 'GRADE_POINT':
        row[column.key] = gradeInfo.point.toFixed(1);
        break;
      case 'WEIGHTED_GRADE_POINT':
        row[column.key] = (gradeInfo.point * seed.unit).toFixed(1);
        break;
      default:
        break;
      }
    });

    return row;
  });
}
